import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDplUndergradMinorsCertificates } from 'apis/dplAPIs';
import { useDispatch, useSelector } from 'react-redux';
import {
  setAllProgramsList,
  setCertificateList,
  setMinorList,
  setProposedChecksheetList,
  setUndergradDegreeList,
} from 'state/slices/dataListsSlice';
import { setChecksheetList } from 'state/slices/dataListsSlice';
import {
  checksheetGetAllByYear,
  checksheetGetAllByYearAndCollege,
  checksheetGetAllProposedByYear,
} from 'apis/checksheetAPIs';
import useServiceAuth from 'auth/useServiceAuth';
import TabPanel from './TabPanel';
import { Button } from '@asu/components-core';
import { Modal } from 'react-bootstrap';
import StatusDefinitions from './StatusDefinitions';
import {
  // idsConcentrationGetAll,
  subplanActivationGetAll,
} from 'apis/adminAPIs';
import ProgramRows from './ProgramRows';
import ProposedRows from './ProposedRows';
import { classNames } from 'utils';
// import ProgramRowsIDS from './ProgramRowsIDS';

const programTypes = {
  undergrad: 0,
  minor: 1,
  cert: 2,
  // ids: 3,
  proposed: 3,
};
const ProgramList = ({ collegeCode, collegeName, programType }) => {
  const { isAuthenticated, getAccessToken } = useServiceAuth();
  const dispatch = useDispatch();

  const [error, setError] = useState('');
  const [showDefinitions, setShowDefinitions] = useState(false);
  const [currentTab, setCurrentTab] = useState(programTypes[programType]);
  // const [checksheetListObject, setChecksheetListObject] = useState({});

  const year = useSelector((state) => state.settings.year);
  const undergradDegreeList = useSelector(
    (state) => state.dataLists.undergradDegreeList
  );
  const minorList = useSelector((state) => state.dataLists.minorList);
  const certificateList = useSelector(
    (state) => state.dataLists.certificateList
  );
  // const idsList = useSelector((state) => state.dataLists.idsConcentrationList);
  const allProgramsList = useSelector(
    (state) => state.dataLists.allProgramsList
  );
  const proposedList = useSelector(
    (state) => state.dataLists.proposedChecksheetList
  );
  // const collegeList = useSelector((state) => state.dataLists.collegeList);
  // const departmentList = useSelector((state) => state.dataLists.departmentList);
  const role = useSelector((state) => state.user.role);
  const asurite = useSelector((state) => state.user.asurite);
  const userCollege = useSelector((state) => state.user.college);
  const userDepartment = useSelector((state) => state.user.department);
  const [showModal, setShowModal] = useState(false);
  const [showProposeButton, setShowProposeButton] = useState(false);

  const userIsProvost = role?.includes('PROVOST');
  const userIsCollege = role?.includes('COLLEGE');
  const isSameCollege = userCollege?.includes(collegeCode);
  const userIsDepartment = role?.includes('DEPARTMENT');

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const shouldShowProposeButton =
    (collegeCode === 'OTHER' && userIsProvost) ||
    (currentTab === 3 &&
      (userIsProvost ||
        ((userIsCollege || userIsDepartment) &&
          (!collegeCode || isSameCollege))));

  useEffect(() => {
    setShowProposeButton(shouldShowProposeButton);
  }, [shouldShowProposeButton]);

  const fetchAllPrograms = useQuery({
    queryKey: ['allPrograms'],
    queryFn: fetchDplUndergradMinorsCertificates,
  });

  const fetchSubplanActivationList = useQuery({
    queryKey: ['subplanActivationList', year],
    queryFn: async () => {
      const token = await getAccessToken();
      return subplanActivationGetAll({ year: year, token });
    },
    enabled: isAuthenticated,
  });

  const {
    data: checksheetData,
    error: getChecksheetError,
    isError: getChecksheetIsError,
    isSuccess: getChecksheetIsSuccess,
    isLoading: getChecksheetIsLoading,
    refetch: refetchChecksheets,
  } = useQuery({
    queryKey: ['checksheets', year, collegeCode],
    queryFn: async () => {
      const token = await getAccessToken();

      if (!!collegeCode)
        return checksheetGetAllByYearAndCollege({
          year,
          college: collegeCode,
          token,
        });
      else return checksheetGetAllByYear({ year, token });
    },
    enabled: !!year,
  });

  const checksheetMap = useMemo(() => {
    if (!checksheetData) return new Map();
    const map = new Map();
    checksheetData.forEach((checksheet) => {
      const sk = checksheet['sk'].S;
      const parts = sk.split('*');
      const key = `${parts[2]}*${parts[3]}`;
      map.set(key, {
        id: sk,
        programType: checksheet['programType'].S,
        checksheetObj: checksheet,
        catalogYear: parts[0],
        status: checksheet['progress']?.S ?? null,
        updatedBy: checksheet['updatedBy']?.S,
        updatedOn: checksheet['updatedOn']?.S,
        lockedBy: checksheet['lockedBy']?.S
          ? JSON.parse(checksheet['lockedBy'].S)
          : {},
        proposed: checksheet['proposed'] ? true : false,
      });
    });
    return map;
  }, [checksheetData]);

  const checksheetMapList = useMemo(
    () => [...checksheetMap.values()],
    [checksheetMap]
  );

  const {
    data: proposedData,
    error: getProposedError,
    isError: getProposedIsError,
    isSuccess: getProposedIsSuccess,
    isLoading: getProposedIsLoading,
    refetch: refetchProposedChecksheets,
  } = useQuery({
    queryKey: ['proposed', year, collegeCode],
    queryFn: async () => {
      const token = await getAccessToken();

      // if (!!collegeCode)
      //   return checksheetGetAllByYearAndCollege({
      //     year,
      //     college: collegeCode,
      //     token,
      //   });
      // else
      return checksheetGetAllProposedByYear({ year, token });
    },
    enabled: !!year,
  });

  // const {
  //   data: idsProgramData,
  //   error: getIdsProgramError,
  //   isError: getIdsProgramIsError,
  //   isSuccess: getIdsProgramIsSuccess,
  //   // isLoading: getIdsProgramIsLoading,
  //   // refetch: refetchIdsPrograms,
  // } = useQuery({
  //   queryKey: ['program', 'LSBISBIS'],
  //   queryFn: () => {
  //     return fetchDplUndergradProgramsByAcadPlanCode('LSBISBIS');
  //   },
  // });

  // const {
  //   data: idsConcentrationsData,
  //   error: getIdsConcentrationsError,
  //   isError: getIdsConcentrationsIsError,
  //   isSuccess: getIdsConcentrationsIsSuccess,
  //   // isLoading: getIdsConcentrationsIsLoading,
  //   // refetch: refetchIdsConcentrations,
  // } = useQuery({
  //   queryKey: ['idsConcentrations', year],
  //   queryFn: async () => {
  //     const token = await getAccessToken();

  //     return idsConcentrationGetAll({ year, token });
  //   },
  //   enabled: !!year,
  // });

  const handleOpenDefinitions = () => setShowDefinitions(true);
  const handleCloseDefinitions = () => setShowDefinitions(false);

  useEffect(() => {
    if (getChecksheetIsError) setError(getChecksheetError.message);

    if (getChecksheetIsSuccess) {
      const next = [];

      checksheetData.forEach((checksheet) => {
        const idYear = checksheet['sk'].S.split('*')[0];
        let lockedBy = {};

        if (!!checksheet['lockedBy']?.S) {
          lockedBy = JSON.parse(checksheet['lockedBy'].S);
        }

        const obj = {
          id: checksheet['sk'].S,
          programType: checksheet['programType'].S,
          checksheetObj: checksheet,
          catalogYear: idYear,
          status: checksheet['progress']?.S ?? null,
          updatedBy: checksheet['updatedBy']?.S,
          updatedOn: checksheet['updatedOn']?.S,
          lockedBy: lockedBy,
          proposed: checksheet['proposed'] ? true : false,
        };

        next.push(obj);
      });

      dispatch(setChecksheetList([...next]));

      // const checkObj = next.reduce((acc, checksheet) => {
      //   acc[checksheet.id] = checksheet;
      //   return acc;
      // }, {});

      // setChecksheetListObject({ ...checkObj });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checksheetData,
    getChecksheetError,
    getChecksheetIsError,
    getChecksheetIsSuccess,
  ]);

  useEffect(() => {
    if (fetchAllPrograms.error) setError(fetchAllPrograms.error.message);
    if (fetchAllPrograms.isSuccess && fetchAllPrograms.fetchStatus === 'idle') {
      const allPrograms = [];
      const undergradPrograms = [];
      const minorPrograms = [];
      const certificatePrograms = [];

      fetchAllPrograms.data.forEach((program) => {
        const obj = {
          acadPlanCode: program.acadPlanCode,
          acadPlanDescription: program.acadPlanMarketingDescription,
          degreeDescriptionShort: program.degreeDescriptionShort,
          campusesOffered: program.campusesOffered,
          acadPlanType: program.acadPlanType,
          owners: program.owners,
          subplan: null,
        };

        allPrograms.push(obj);

        if (program.acadPlanType === 'MIN') {
          minorPrograms.push(obj);
        } else if (
          program.acadPlanType === 'CER' ||
          program.acadPlanType === 'SAC'
        ) {
          certificatePrograms.push(obj);
        } else {
          undergradPrograms.push(obj);
        }

        if (program.subplans && fetchSubplanActivationList.isSuccess) {
          program.subplans.forEach((subplan) => {
            if (
              fetchSubplanActivationList.data?.[program.acadPlanCode]?.[
                subplan.acadSubPlanCode
              ]
            ) {
              const subplanObj = {
                acadPlanCode: program.acadPlanCode,
                acadPlanDescription: program.acadPlanDescription,
                degreeDescriptionShort: program.degreeDescriptionShort,
                acadPlanType: program.acadPlanType,
                campusesOffered: program.campusesOffered,
                owners: program.owners,
                subplan: subplan,
              };

              allPrograms.push(subplanObj);

              if (program.acadPlanType === 'MIN') {
                minorPrograms.push(subplanObj);
              } else if (
                program.acadPlanType === 'CER' ||
                program.acadPlanType === 'SAC'
              ) {
                certificatePrograms.push(subplanObj);
              } else {
                undergradPrograms.push(subplanObj);
              }
            }
          });
        }
      });

      if (fetchSubplanActivationList.isError)
        setError(fetchSubplanActivationList.error.message);

      dispatch(setAllProgramsList(allPrograms));
      dispatch(setUndergradDegreeList(undergradPrograms));
      dispatch(setMinorList(minorPrograms));
      dispatch(setCertificateList(certificatePrograms));
    }
  }, [
    dispatch,
    fetchAllPrograms?.data,
    fetchAllPrograms?.error,
    fetchAllPrograms?.fetchStatus,
    fetchAllPrograms?.isSuccess,
    fetchSubplanActivationList.data,
    fetchSubplanActivationList.error,
    fetchSubplanActivationList.isError,
    fetchSubplanActivationList.isSuccess,
  ]);

  useEffect(() => {
    if (getProposedIsError) setError(getProposedError.message);

    if (getProposedIsSuccess) {
      const next = [];

      proposedData.forEach((checksheet) => {
        const idYear = checksheet['sk'].S.split('*')[0];
        let lockedBy = {};

        if (!!checksheet['lockedBy']?.S) {
          lockedBy = JSON.parse(checksheet['lockedBy'].S);
        }

        const obj = {
          id: checksheet['sk'].S,
          programType: checksheet['programType'].S,
          checksheetObj: checksheet,
          catalogYear: idYear,
          status: checksheet['progress']?.S ?? null,
          updatedBy: checksheet['updatedBy']?.S,
          updatedOn: checksheet['updatedOn']?.S,
          lockedBy: lockedBy,
          checksheetName: checksheet['checksheetName'],
          proposed: checksheet['proposed'] ? true : false,
        };

        next.push(obj);
      });

      dispatch(setProposedChecksheetList([...next]));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    proposedData,
    getProposedError,
    getProposedIsError,
    getProposedIsSuccess,
  ]);

  // useEffect(() => {
  //   if (getIdsProgramIsError) setError(getIdsProgramError.message);
  //   if (getIdsConcentrationsIsError)
  //     setError(getIdsConcentrationsError.message);

  //   if (getIdsProgramIsSuccess && getIdsConcentrationsIsSuccess) {
  //     const next = [];

  //     idsProgramData.subplans
  //       .filter((subplan) => subplan.acadSubplanTypeCode === 'CON')
  //       .forEach((program) => {
  //         const obj = {
  //           acadPlanCode: 'LSBISBIS',
  //           acadPlanDescription: program.description,
  //           acadPlanType: program.acadSubplanTypeCode,
  //           owner: idsConcentrationsData[program.acadSubPlanCode],
  //           subplan: { acadSubPlanCode: program.acadSubPlanCode },
  //         };

  //         next.push(obj);
  //       });

  //     dispatch(setIdsConcentrationList([...next]));
  //   }
  // }, [
  //   dispatch,
  //   getIdsConcentrationsError,
  //   getIdsConcentrationsIsError,
  //   getIdsConcentrationsIsSuccess,
  //   getIdsProgramError,
  //   getIdsProgramIsError,
  //   getIdsProgramIsSuccess,
  //   idsConcentrationsData,
  //   idsProgramData,
  // ]);

  if (getChecksheetIsError || getProposedIsError || fetchAllPrograms.isError)
    return (
      <div>
        <div>An error has occurred: {error}</div>
        <div>
          Please refresh the page. If the error persists, please try again
          later.
        </div>
      </div>
    );

  return (
    <>
      <div>
        <div
          className={classNames(
            showProposeButton && 'visible',
            'propose-program-button d-flex justify-content-end px-2 py-1'
          )}
        >
          <Button
            label="Propose new program"
            color="maroon"
            onClick={handleOpenModal}
          />
        </div>
        {collegeCode === 'OTHER' ? (
          <TabPanel
            tabs={['Proposed']}
            largeText={true}
            initialTab={0}
            currentTab={0}
            setCurrentTab={setCurrentTab}
          >
            <ProposedRows
              degreeType={'proposed'}
              collegeCode={collegeCode}
              collegeName={collegeName}
              programList={allProgramsList}
              fetchPrograms={fetchAllPrograms}
              checksheetList={proposedList}
              refetchChecksheets={refetchProposedChecksheets}
              getChecksheetIsLoading={getProposedIsLoading}
              setError={setError}
              handleOpenDefinitions={handleOpenDefinitions}
              user={{
                role,
                asurite,
                college: userCollege,
                department: userDepartment,
              }}
              year={year}
              showModal={showModal}
              setShowModal={setShowModal}
              handleCloseModal={handleCloseModal}
            />
            <></>
          </TabPanel>
        ) : (
          <TabPanel
            tabs={[
              'Undergraduate Degrees',
              'Minors',
              'Certificates',
              // 'IDS Concentrations',
              'Proposed',
            ]}
            largeText={true}
            initialTab={programTypes[programType]}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
          >
            <ProgramRows
              degreeType={'undergrad'}
              collegeCode={collegeCode}
              collegeName={collegeName}
              programList={undergradDegreeList}
              fetchPrograms={fetchAllPrograms}
              fetchSubplanActivationList={fetchSubplanActivationList}
              checksheetList={checksheetMapList}
              checksheetMap={checksheetMap}
              refetchChecksheets={refetchChecksheets}
              getChecksheetIsLoading={getChecksheetIsLoading}
              getChecksheetIsSuccess={getChecksheetIsSuccess}
              setError={setError}
              handleOpenDefinitions={handleOpenDefinitions}
              user={{
                role,
                asurite,
                college: userCollege,
                department: userDepartment,
              }}
              year={year}
            />
            <ProgramRows
              degreeType={'minor'}
              collegeCode={collegeCode}
              collegeName={collegeName}
              programList={minorList}
              fetchPrograms={fetchAllPrograms}
              checksheetList={checksheetMapList}
              checksheetMap={checksheetMap}
              refetchChecksheets={refetchChecksheets}
              getChecksheetIsLoading={getChecksheetIsLoading}
              getChecksheetIsSuccess={getChecksheetIsSuccess}
              setError={setError}
              handleOpenDefinitions={handleOpenDefinitions}
              user={{
                role,
                asurite,
                college: userCollege,
                department: userDepartment,
              }}
              year={year}
            />
            <ProgramRows
              degreeType={'cert'}
              collegeCode={collegeCode}
              collegeName={collegeName}
              programList={certificateList}
              fetchPrograms={fetchAllPrograms}
              checksheetList={checksheetMapList}
              checksheetMap={checksheetMap}
              refetchChecksheets={refetchChecksheets}
              getChecksheetIsLoading={getChecksheetIsLoading}
              getChecksheetIsSuccess={getChecksheetIsSuccess}
              setError={setError}
              handleOpenDefinitions={handleOpenDefinitions}
              user={{
                role,
                asurite,
                college: userCollege,
                department: userDepartment,
              }}
              year={year}
            />
            {/* <ProgramRowsIDS
              degreeType={'ids'}
              collegeCode={collegeCode}
              collegeName={collegeName}
              programList={idsList}
              fetchPrograms={fetchUndergradDegrees}
              allProgramsList={allProgramsList}
              checksheetList={checksheetList}
              collegeList={collegeList}
              departmentList={departmentList}
              refetchChecksheets={refetchChecksheets}
              getChecksheetIsLoading={getChecksheetIsLoading}
              setError={setError}
              handleOpenDefinitions={handleOpenDefinitions}
              user={{
                role,
                asurite,
                college: userCollege,
                department: userDepartment,
              }}
              year={year}
              checksheetListObj={checksheetListObject}
            /> */}
            <ProposedRows
              degreeType={'proposed'}
              collegeCode={collegeCode}
              collegeName={collegeName}
              programList={allProgramsList}
              fetchPrograms={fetchAllPrograms}
              checksheetList={proposedList}
              refetchChecksheets={refetchProposedChecksheets}
              getChecksheetIsLoading={getChecksheetIsLoading}
              setError={setError}
              handleOpenDefinitions={handleOpenDefinitions}
              user={{
                role,
                asurite,
                college: userCollege,
                department: userDepartment,
              }}
              year={year}
              showModal={showModal}
              setShowModal={setShowModal}
              handleCloseModal={handleCloseModal}
            />
          </TabPanel>
        )}
      </div>
      <Modal show={showDefinitions} onHide={handleCloseDefinitions}>
        <StatusDefinitions
          showNoChecksheet={true}
          handleClose={handleCloseDefinitions}
        />
      </Modal>
    </>
  );
};

export default ProgramList;
